import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InputField from '../../../components/form/InputField';
import SubmitButton from '../../../components/form/SubmitButton';
import LoadingButton from '../../../components/form/LoadingButton';
import { apiPost } from '../../../utils/api';
import { showError, showSuccess } from '../../../utils/alert';
import { Store } from '../../../types/modules/store.type';
import { useDispatch } from 'react-redux';
import { getDeviceId } from "../../../api/token";
import { resetStoreForm } from '../../../redux/storeFormSlice';
import { adaptStoreLogin } from '../../../redux/authAdapter';
import AdvancedPhoneField from '../../../components/form/AdvancedPhoneField';

interface LoginFormData {
  phone: string;
  password: string;
}

export function StoreLoginForm() {
  const { t, i18n } = useTranslation();
  const { handleSubmit, register, formState: { errors } } = useForm<LoginFormData>();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const isRTL = i18n.language === 'ar';

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      const deviceId = getDeviceId();
      const response = await apiPost('store/login', { phone: `${data.phone}`, password: data.password, deviceId }) as { accessToken: string, refreshToken: string, store: Store };
      showSuccess(t("loginSuccess"));
      dispatch(resetStoreForm());
      dispatch(adaptStoreLogin({
        accessToken: response.accessToken,
        store: response.store
      }) as any);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <AdvancedPhoneField
        placeholder={t('login.phonePlaceholder')}
        register={register('phone', { required: t('login.phoneRequired') })}
        error={errors.phone}
      />

      <InputField
        placeholder={t('login.passwordPlaceholder')}
        type="password"
        register={register('password', { required: t('login.passwordRequired') })}
        error={errors.password}
      />

      {loading
        ? <LoadingButton height={64} />
        : <SubmitButton height={64} label={t('login_btn')} />
      }
    </form>
  );
}

export default function StoreLogin() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen w-screen flex flex-col md:flex-row bg-light overflow-hidden">
      {/* Image Section */}
      <div className="hidden md:block md:w-1/2 h-screen">
        <img
          src={"/main.png"}
          alt="Login"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Form Section */}
      <div className="flex flex-1 justify-center items-center p-8 md:p-16 min-h-screen">
        <div className="w-full max-w-md p-6 md:p-8">
          <h1 className="text-2xl text-center font-bold text-Black mb-2">{t('login.title')}</h1>
          <StoreLoginForm />
        </div>
      </div>
    </div>
  );
}
